<template>
  <div class="main-container p-3">
    <KidHeader :profileId="profileId" :serialNumber="serialNumber" />

    <!-- Back to Schedule List -->
    <div class="back-home text-left mt-3">
      <a class="text-black" href="javascript:void(0)" @click="$router.push({ path: '/schedule', query: { profileId: this.profileId, serialNumber: serialNumber } })">
        <i class="fas fa-arrow-left"></i> <b>Back to Schedules</b>
      </a>
    </div>

    <!-- Conflict Warning Alert -->
    <div v-if="conflictWarning.show" class="alert alert-danger alert-dismissible fade show" role="alert">
      <h6 class="alert-heading"><i class="fas fa-exclamation-triangle"></i> Schedule Conflicts Detected</h6>
      <ul class="mb-0">
        <li v-for="(conflict, index) in conflictWarning.conflicts" :key="index">
          {{ conflict.message }}
        </li>
      </ul>
      <button type="button" class="close" @click="conflictWarning.show = false">
        <span aria-hidden="true">&times;</span>
      </button>
    </div>

    <!-- Warning Alert (non-blocking) -->
    <div v-if="warningAlert.show" class="alert alert-warning alert-dismissible fade show" role="alert">
      <h6 class="alert-heading"><i class="fas fa-exclamation-circle"></i> Warning</h6>
      <ul class="mb-0">
        <li v-for="(warning, index) in warningAlert.warnings" :key="index">
          {{ warning.message }}
        </li>
      </ul>
      <button type="button" class="close" @click="warningAlert.show = false">
        <span aria-hidden="true">&times;</span>
      </button>
    </div>

    <!-- Loading Overlay -->
    <div v-if="isLoading" class="loading-overlay">
      <div class="spinner-border text-primary" role="status">
        <span class="sr-only">Loading...</span>
      </div>
      <p class="mt-2">{{ loadingMessage }}</p>
    </div>

    <!-- Schedule Form -->
    <div class="schedule-form">
      <div class="card">
        <div class="card-body">

          <!-- Schedule Label -->
          <div class="form-group mb-3 text-left">
            <label class="font-weight-bold">
              <i class="fas fa-tag"></i> Schedule Name <span class="text-danger">*</span>
            </label>
            <input
              autofocus
              type="text"
              class="form-control form-control-sm"
              v-model="currentSchedule.label"
              placeholder="e.g., School Time, Bedtime, Weekend Gaming"
              :class="{ 'is-invalid': errors.label }"
            />
            <small class="text-muted">Give your schedule a short descriptive name</small>
            <div v-if="errors.label" class="invalid-feedback">{{ errors.label }}</div>
          </div>

          <!-- Schedule Type Selector (compact) -->
          <div class="form-group mb-3 text-left">
            <label class="font-weight-bold d-block mb-2">
              <i class="fas fa-layer-group"></i> Schedule Type <span class="text-danger">*</span>
            </label>

            <div class="type-chips">
              <button
                type="button"
                class="type-chip"
                :class="{ active: currentSchedule.type === 'custom' }"
                @click="selectScheduleType('custom')"
                title="Custom - choose days manually"
              >
                <i class="fas fa-sliders-h"></i>
                <div class="chip-label">Custom</div>
              </button>

              <button
                type="button"
                class="type-chip"
                :class="{ active: currentSchedule.type === 'weekday' }"
                @click="selectScheduleType('weekday')"
                title="Mon - Fri"
              >
                <i class="fas fa-briefcase"></i>
                <div class="chip-label">Weekday</div>
              </button>

              <button
                type="button"
                class="type-chip"
                :class="{ active: currentSchedule.type === 'weekend' }"
                @click="selectScheduleType('weekend')"
                title="Sat - Sun"
              >
                <i class="fas fa-calendar-week"></i>
                <div class="chip-label">Weekend</div>
              </button>
            </div>
          </div>

          <!-- Day Selection (modern bubbles) -->
          <div class="form-group mb-3 text-left">
            <label class="font-weight-bold d-block mb-2">
              <i class="fas fa-calendar-day"></i> Select Days <span class="text-danger">*</span>
            </label>

            <div class="days-bubbles">
              <button
                type="button"
                v-for="day in allDays"
                :key="day.value"
                class="day-bubble"
                :class="{ active: currentSchedule.days.includes(day.value) }"
                @click="toggleDay(day.value)"
              >
                {{ day.short }}
              </button>
            </div>

            <div class="mt-2">
              <small v-if="errors.days" class="text-danger">{{ errors.days }}</small>
              <small v-else class="text-muted">Tap days to include them in the schedule.</small>
            </div>
          </div>

          <!-- Restriction Settings (Multiple) -->
          <div class="form-group mb-3 text-left">
            <label class="font-weight-bold d-block mb-2">
              <i class="fas fa-clock"></i> Restriction Settings <span class="text-danger">*</span>
            </label>

            <!-- Loop through slots -->
            <div v-for="(slot, index) in currentSchedule.slots" :key="index" class="compact-slot mb-3">
              <!-- Slot Header with Delete Button -->
              <div class="slot-header" v-if="currentSchedule.slots.length > 1">
                <span class="slot-number">Restriction #{{ index + 1 }}</span>
                <button type="button" class="btn-delete-slot" @click="removeSlot(index)">
                  <i class="fas fa-trash-alt"></i>
                </button>
              </div>

              <!-- 1. RESTRICTION MODE -->
              <div>
                <label class="small font-weight-bold d-block mb-1">Restriction Mode</label>
                <div class="compact-mode">
                  <button
                    type="button"
                    class="mode-btn sleep-btn"
                    :class="{ active: slot.mode === 'sleep' }"
                    @click="slot.mode = 'sleep'"
                  >
                    <i class="fas fa-ban"></i> Sleep Mode
                  </button>

                  <button
                    type="button"
                    class="mode-btn apps-btn"
                    :class="{ active: slot.mode === 'apps' }"
                    @click="slot.mode = 'apps'"
                  >
                    <i class="fas fa-mobile-alt"></i> Apps Restriction
                  </button>

                  <button
                    disabled
                    type="button"
                    class="mode-btn limit-btn disabled-mode"
                    :class="{ active: slot.mode === 'limit' }"
                    @click="slot.mode = 'limit'"
                  >
                    <i class="fas fa-hourglass-half"></i> Daily Limit
                    <span class="coming-soon-badge">Coming Soon</span>
                  </button>
                </div>
              </div>

              <!-- 2. MODE DESCRIPTION / NOTE -->
              <div class="mt-2">
                <div v-if="slot.mode === 'sleep'" class="mode-note mode-note-sleep">
                  <i class="fas fa-info-circle"></i>
                  <span><strong>Sleep Mode:</strong> All apps will be disabled during the selected time period.</span>
                </div>
                <div v-else-if="slot.mode === 'limit'" class="mode-note mode-note-limit">
                  <i class="fas fa-info-circle"></i>
                  <span><strong>Daily Limit:</strong> All apps will be disabled once the total usage time is reached.</span>
                </div>
                <div v-else class="mode-note mode-note-apps">
                  <i class="fas fa-info-circle"></i>
                  <span><strong>Apps Restriction:</strong> Selected app will be disabled during the specified time period.</span>
                </div>
              </div>

              <!-- 3. TIME FIELDS (FOR SLEEP MODE AND APPS RESTRICTION) -->
              <div v-if="slot.mode === 'sleep' || slot.mode === 'apps'" class="mt-3">
                <div class="slot-row">
                  <div class="slot-col">
                    <label class="small font-weight-bold">From</label>
                    <input type="time" class="form-control form-control-sm" v-model="slot.startTime" />
                  </div>
                  <div class="slot-col">
                    <label class="small font-weight-bold">To</label>
                    <input type="time" class="form-control form-control-sm" v-model="slot.endTime" />
                  </div>
                </div>
                
                <!-- Overnight Schedule Info -->
                <div v-if="isOvernightRestriction(slot)" class="overnight-info-create">
                  <!-- Header -->
                  <div class="overnight-header">
                    <i class="fas fa-moon"></i>
                    <strong>Overnight Schedule</strong>
                  </div>
                  
                  <!-- Content -->
                  <div class="overnight-text">
                    <p>This schedule starts on the selected days and continues until the next morning.</p>
                    
                    <!-- Example Box -->
                    <div class="overnight-example">
                      <div class="example-label">
                        <i class="fas fa-lightbulb"></i>
                        <span>Example</span>
                      </div>
                      <p class="example-text">
                        If you select <strong>Monday-Friday</strong>, the Friday schedule will run from 
                        <strong>{{ formatTimePreview(slot.startTime) }}</strong> on Friday to 
                        <strong>{{ formatTimePreview(slot.endTime) }}</strong> on Saturday morning.
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              <!-- 4. LIMIT INPUT (ONLY FOR DAILY LIMIT MODE) -->
              <div v-if="slot.mode === 'limit'" class="mt-3">
                <label class="small font-weight-bold d-block mb-1">Daily Usage Limit (minutes)</label>
                <input
                  type="number"
                  class="form-control form-control-sm"
                  v-model.number="slot.limit"
                  min="5"
                  max="480"
                  step="5"
                  placeholder="e.g., 60"
                />
                <small class="text-muted d-block mt-1">Minimum 5 minutes, Maximum 480 minutes (8 hours)</small>
              </div>

              <!-- 5. APP CATEGORIES (ONLY FOR APPS RESTRICTION MODE) -->
              <!-- 5. APP LIST (ONLY FOR APPS RESTRICTION MODE) -->
              <div v-if="slot.mode === 'apps'" class="mt-3">
                <label class="small font-weight-bold d-block mb-2">Select Apps to Restrict</label>

                <!-- Loading -->
                <div v-if="installedAppsLoading" class="apps-flat-loader">
                  <div class="spinner-border spinner-border-sm text-primary" role="status">
                    <span class="sr-only">Loading...</span>
                  </div>
                  <span class="ml-2" style="font-size:13px; color:#6b7280;">Loading apps...</span>
                </div>

                <!-- Empty -->
                <div v-else-if="installedApps.length === 0" class="apps-flat-empty">
                  <i class="fas fa-mobile-alt"></i>
                  <span>No apps found on this device</span>
                </div>

                <!-- List -->
                <div v-else class="apps-flat-list">
                  <div
                    v-for="app in installedApps"
                    :key="app.id"
                    class="app-flat-item"
                  >
                    <div class="app-flat-info">
                      <img :src="app.appIcon" :alt="app.name" class="app-flat-icon-img" />
                      <span class="app-name">{{ app.name }}</span>
                    </div>
                    <div class="custom-control custom-switch">
                      <input
                        type="checkbox"
                        class="custom-control-input"
                        :id="'app-' + index + '-' + app.id"
                        v-model="slot.restrictedApps"
                        :value="app.packageName"
                      />
                      <label class="custom-control-label" :for="'app-' + index + '-' + app.id"></label>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Add More Restriction Button -->
            <button type="button" class="btn-add-more" @click="addSlot()">
              <i class="fas fa-plus-circle"></i> Add More Restriction
            </button>

            <div class="mt-2">
              <small v-if="errors.slots" class="text-danger">{{ errors.slots }}</small>
            </div>
          </div>

          <!-- Schedule Status -->
          <div class="form-group mb-0 text-left">
            <div class="custom-control custom-switch">
              <input
                type="checkbox"
                class="custom-control-input"
                id="scheduleActive"
                v-model="currentSchedule.isActive"
              />
              <label class="custom-control-label font-weight-bold" for="scheduleActive">
                <i class="fas fa-power-off"></i> Activate this schedule immediately
              </label>
            </div>
            <small class="text-muted d-block mt-1">You can enable or disable this schedule anytime from the list</small>
          </div>

        </div>
      </div>

      <!-- Action Buttons -->
      <div class="action-buttons mt-3 d-flex justify-content-end">
        <button
          type="button"
          class="btn btn-secondary btn-sm"
          @click="cancelSchedule()"
          :disabled="isLoading"
        >
          <i class="fas fa-times"></i> Cancel
        </button>

        <button
          type="button"
          class="btn btn-primary btn-sm ml-2"
          @click="saveSchedule()"
          :disabled="!isValidSchedule || isLoading"
        >
          <i class="fas fa-save"></i> {{ isEditMode ? 'Update Schedule' : 'Save Schedule' }}
        </button>
      </div>
    </div>

    <KidFooter :profileId="profileId" :serialNumber="serialNumber" />
  </div>
</template>

<script>
import KidHeader from '@/components/KidHeader.vue'
import KidFooter from '@/components/KidFooter.vue'
import ScheduleService from "../../services/ScheduleService.js"
import ProfileService from "../../services/ProfileService.js"

export default {
  name: 'CreateSchedule',
  components: { KidHeader, KidFooter },
  data() {
    return {
      profileId: this.$route.query.profileId,
      serialNumber: this.$route.query.serialNumber,
      scheduleId: this.$route.query.scheduleId,
      isEditMode: this.$route.query.edit === 'true',
      isLoading: false,
      loadingMessage: 'Processing...',
      conflictWarning: {
        show: false,
        conflicts: []
      },
      warningAlert: {
        show: false,
        warnings: []
      },
      currentSchedule: {
        label: 'Schedule 1',
        type: 'custom',
        days: [],
        slots: [{ startTime: '09:00', endTime: '17:00', mode: 'sleep', limit: 60, categories: [], restrictedApps: [] }],
        isActive: true
      },
      allDays: [
        { label: 'Monday', short: 'Mo', value: 'monday' },
        { label: 'Tuesday', short: 'Tu', value: 'tuesday' },
        { label: 'Wednesday', short: 'We', value: 'wednesday' },
        { label: 'Thursday', short: 'Th', value: 'thursday' },
        { label: 'Friday', short: 'Fr', value: 'friday' },
        { label: 'Saturday', short: 'Sa', value: 'saturday' },
        { label: 'Sunday', short: 'Su', value: 'sunday' }
      ],
      installedApps: [],
      installedAppsLoading: false,  
      expandedCategories: {},
      errors: {}
    };
  },
  computed: {
    isValidSchedule() {
      return this.currentSchedule.label.trim() !== '' &&
             this.currentSchedule.days.length > 0 &&
             this.currentSchedule.slots.length > 0 &&
             this.validateAllSlots();
    }
  },
  methods: {
    async fetchInstalledApps() {
      this.installedAppsLoading = true;
      try {
        const response = await ProfileService.getDeviceProfileApplicationList({
          profile_id: this.profileId,
          install_type: "FORCE_INSTALLED",
          serialNumber: this.serialNumber
        });
        this.installedApps = response.data.data
        .filter(app => app.package_name !== "com.mywingio.dialer")
        .map(app => ({
          id: app._id,
          name: app.display_text,
          packageName: app.package_name,
          appIcon: app.app_icon
        }));
        
      } catch (e) {
        this.$toast.error(e.response?.data?.respones_msg || 'Failed to load apps');
      } finally {
        this.installedAppsLoading = false;
      }
    },
    selectScheduleType(type) {
      this.currentSchedule.type = type;
      switch(type) {
        case 'weekday':
          this.currentSchedule.days = ['monday','tuesday','wednesday','thursday','friday'];
          break;
        case 'weekend':
          this.currentSchedule.days = ['saturday','sunday'];
          break;
        case 'everyday':
          this.currentSchedule.days = ['monday','tuesday','wednesday','thursday','friday','saturday','sunday'];
          break;
        case 'custom':
          this.currentSchedule.days = [];
          break;
        default:
          break;
      }
    },
    toggleDay(day) {
      const idx = this.currentSchedule.days.indexOf(day);
      if (idx > -1) this.currentSchedule.days.splice(idx, 1);
      else this.currentSchedule.days.push(day);
      this.currentSchedule.type = 'custom';
    },
    addSlot() {
      this.currentSchedule.slots.push({
        startTime: '09:00',
        endTime: '17:00',
        mode: 'sleep',
        limit: 60,
        categories: [],
        restrictedApps: []
      });
    },
    removeSlot(index) {
      if (this.currentSchedule.slots.length > 1) {
        this.currentSchedule.slots.splice(index, 1);
      }
    },
    toggleCategoryExpand(slotIndex, categoryId) {
      const key = slotIndex + '-' + categoryId;
      this.expandedCategories[key] = !this.expandedCategories[key];
    },
    validateAllSlots() {
      return this.currentSchedule.slots.every(slot => {
        if (slot.mode === 'sleep') {
          return slot.startTime && slot.endTime;
        } else if (slot.mode === 'limit') {
          return slot.limit && slot.limit >= 5;
        } else if (slot.mode === 'apps') {
          return slot.startTime && slot.endTime && slot.restrictedApps.length > 0;
        }
        return false;
      });
    },
    validateSchedule() {
      this.errors = {};
      if (!this.currentSchedule.label.trim()) this.errors.label = 'Schedule name is required';
      if (this.currentSchedule.days.length === 0) this.errors.days = 'Please select at least one day';
      
      // Validate each slot
      let hasError = false;
      this.currentSchedule.slots.forEach((slot, index) => {
        if (slot.mode === 'sleep' && (!slot.startTime || !slot.endTime)) {
          this.errors.slots = `Restriction #${index + 1}: Start time and end time are required`;
          hasError = true;
        }
        if (slot.mode === 'limit' && (!slot.limit || slot.limit < 5)) {
          this.errors.slots = `Restriction #${index + 1}: Daily limit must be at least 5 minutes`;
          hasError = true;
        }
        if (slot.mode === 'apps') {
          if (!slot.startTime || !slot.endTime) {
            this.errors.slots = `Restriction #${index + 1}: Start time and end time are required`;
            hasError = true;
          }
          if (slot.restrictedApps.length === 0) {          // ← was slot.categories.length
            this.errors.slots = `Restriction #${index + 1}: Please select at least one app`;
            hasError = true;
          }
        }
      });
      
      return Object.keys(this.errors).length === 0;
    },
    buildSchedulePayload() {
      return {
        profileId: this.profileId,
        serialNumber: this.serialNumber,
        schedule: {
          id: this.isEditMode ? this.scheduleId : null,
          label: this.currentSchedule.label.trim(),
          type: this.currentSchedule.type,
          days: this.currentSchedule.days,
          isActive: this.currentSchedule.isActive,
          restrictions: this.currentSchedule.slots.map((slot, index) => {
            const restriction = {
              order: index + 1,
              mode: slot.mode
            };

            // Add mode-specific fields
            if (slot.mode === 'sleep') {
              restriction.startTime = slot.startTime;
              restriction.endTime = slot.endTime;
            } else if (slot.mode === 'limit') {
              restriction.dailyLimitMinutes = slot.limit;
            } else if (slot.mode === 'apps') {
              restriction.startTime = slot.startTime;
              restriction.endTime = slot.endTime;
              restriction.restrictedApps = slot.restrictedApps || [];
            }

            return restriction;
          })
        }
      };
    },
    async saveSchedule() {
      // Clear previous warnings/errors
      this.conflictWarning.show = false;
      this.warningAlert.show = false;
      
      // Validate form
      if (!this.validateSchedule()) {
        this.$toast.error('Please fix the validation errors before saving');
        return;
      }
      
      // Build payload
      const schedulePayload = this.buildSchedulePayload();
      
      console.log('Schedule Payload:', JSON.stringify(schedulePayload, null, 2));
      
      // Show loading
      this.isLoading = true;
      this.loadingMessage = this.isEditMode ? 'Updating schedule...' : 'Creating schedule...';
      
      try {
        let response;
        
        // Call appropriate API method
        if (this.isEditMode) {
          response = await ScheduleService.updateSchedule(schedulePayload);
        } else {
          response = await ScheduleService.createSchedule(schedulePayload);
        }
        
        // Check response
        if (response.data.success) {
          // Show warnings if any (non-blocking)
          if (response.data.warnings && response.data.warnings.length > 0) {
            this.warningAlert.warnings = response.data.warnings;
            this.warningAlert.show = true;
            
            // Auto-hide warning after 8 seconds
            setTimeout(() => {
              this.warningAlert.show = false;
            }, 8000);
          }
          
          // Show success message
          //this.$toast.success(response.data.message || (this.isEditMode ? 'Schedule updated successfully!' : 'Schedule created successfully!'));
          
          // Wait a bit to show warnings if any, then redirect
          setTimeout(() => {
            this.$router.push({ 
              path: '/schedule', 
              query: { 
                profileId: this.profileId, 
                serialNumber: this.serialNumber 
              } 
            });
          }, this.warningAlert.show ? 1000 : 500);
        } else {
          // Handle unexpected success:false
          this.$toast.error(response.data.message || 'Failed to save schedule');
        }
        
      } catch (error) {
        console.error('Error saving schedule:', error);
        
        // Check if it's a conflict error (409)
        if (error.response && error.response.status === 409) {
          const data = error.response.data;
          
          // Show conflicts
          if (data.conflicts && data.conflicts.length > 0) {
            this.conflictWarning.conflicts = data.conflicts;
            this.conflictWarning.show = true;
            
            // Scroll to top to show conflict warning
            window.scrollTo({ top: 0, behavior: 'smooth' });
          }
          
          // Show warnings if any
          if (data.warnings && data.warnings.length > 0) {
            this.warningAlert.warnings = data.warnings;
            this.warningAlert.show = true;
          }
          
          this.$toast.error('Cannot save schedule due to conflicts. Please resolve them first.');
        } 
        // Handle validation errors (400)
        else if (error.response && error.response.status === 400) {
          const errorMessage = error.response.data.message || 'Validation error';
          this.$toast.error(errorMessage);
          
          // Try to map error to specific field if possible
          if (errorMessage.toLowerCase().includes('label')) {
            this.errors.label = errorMessage;
          } else if (errorMessage.toLowerCase().includes('days')) {
            this.errors.days = errorMessage;
          } else if (errorMessage.toLowerCase().includes('restriction')) {
            this.errors.slots = errorMessage;
          }
        }
        // Handle other errors
        else {
          const errorMessage = error.response?.data?.message || error.message || 'Failed to save schedule';
          this.$toast.error(errorMessage);
        }
      } finally {
        this.isLoading = false;
      }
    },
    cancelSchedule() {
      if (confirm('Are you sure you want to cancel? Any unsaved changes will be lost.')) {
        this.$router.push({ 
          path: '/schedule', 
          query: { 
            profileId: this.profileId, 
            serialNumber: this.serialNumber 
          } 
        });
      }
    },
    async loadScheduleForEdit() {
      if (!this.isEditMode || !this.scheduleId) return;
      
      this.isLoading = true;
      this.loadingMessage = 'Loading schedule...';
      
      try {
        const response = await ScheduleService.getScheduleById(
          this.scheduleId,
          this.profileId,
          this.serialNumber
        );
        
        if (response.data.success && response.data.data) {
          const schedule = response.data.data;
          
          // Map API data to component data structure
          this.currentSchedule = {
            label: schedule.label,
            type: schedule.type,
            days: schedule.days,
            isActive: schedule.isActive,
            slots: schedule.restrictions.map(restriction => {
              const slot = {
                mode: restriction.mode,
                startTime: restriction.startTime || '09:00',
                endTime: restriction.endTime || '17:00',
                limit: restriction.dailyLimitMinutes || 60,
                categories: restriction.categories || [],
                restrictedApps: restriction.restrictedApps || []
              };
              return slot;
            })
          };
        }
      } catch (error) {
        console.error('Error loading schedule:', error);
        this.$toast.error('Failed to load schedule details');
        
        // Redirect back to list on error
        setTimeout(() => {
          this.$router.push({ 
            path: '/schedule', 
            query: { 
              profileId: this.profileId, 
              serialNumber: this.serialNumber 
            } 
          });
        }, 2000);
      } finally {
        this.isLoading = false;
      }
    },
    isOvernightRestriction(slot) {
      if (!slot.startTime || !slot.endTime) return false;
      
      const [startHour, startMin] = slot.startTime.split(':').map(Number);
      const [endHour, endMin] = slot.endTime.split(':').map(Number);
      
      const startMinutes = startHour * 60 + startMin;
      const endMinutes = endHour * 60 + endMin;
      
      return endMinutes < startMinutes;
    },

    formatTimePreview(time) {
      if (!time) return '';
      const [hours, minutes] = time.split(':');
      const hour = parseInt(hours);
      const ampm = hour >= 12 ? 'PM' : 'AM';
      const hour12 = hour % 12 || 12;
      return `${hour12}:${minutes} ${ampm}`;
    },
  },
  mounted() {
    this.fetchInstalledApps();  // ← add this
    // Load schedule data if in edit mode
    if (this.isEditMode) {
      this.loadScheduleForEdit();
    }
  }
};
</script>

<style scoped>
/* ---------- Base layout ---------- */
.back-home {
  display: block;
  width: 100%;
  margin-bottom: 12px;
  font-size: 0.95rem;
}

.card {
  border: none;
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.06);
}

/* ---------- Loading Overlay ---------- */
.loading-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(255, 255, 255, 0.9);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  z-index: 9999;
}

.loading-overlay p {
  margin-top: 15px;
  font-weight: 600;
  color: #6C63FF;
}

/* ---------- Alert Styles ---------- */
.alert {
  border-radius: 10px;
  border: none;
  box-shadow: 0 2px 8px rgba(0,0,0,0.1);
}

.alert-danger {
  background: #fee;
  color: #991b1b;
}

.alert-warning {
  background: #fffbeb;
  color: #92400e;
}

.alert-heading {
  font-size: 14px;
  font-weight: 700;
  margin-bottom: 10px;
}

.alert ul {
  padding-left: 20px;
  margin-bottom: 0;
}

.alert li {
  margin-bottom: 5px;
  font-size: 13px;
}

/* ---------- Type Chips ---------- */
.type-chips {
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
}

.type-chip {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 12px;
  border-radius: 999px;
  border: 1px solid #e6e6ee;
  background: #fafafa;
  cursor: pointer;
  font-size: 14px;
  transition: all 0.18s ease;
  min-width: 92px;
  justify-content: center;
  color: #333;
}

.type-chip i {
  font-size: 14px;
  opacity: 0.9;
}

.type-chip .chip-label {
  font-weight: 600;
  font-size: 13px;
}

.type-chip:hover {
  transform: translateY(-3px);
  box-shadow: 0 6px 12px rgba(102,103,255,0.08);
}

.type-chip.active {
  background: linear-gradient(90deg,#6C63FF,#5E54E2);
  color: #fff;
  border-color: rgba(92,80,255,0.9);
  box-shadow: 0 8px 20px rgba(92,80,255,0.12);
}

/* ---------- Days bubbles ---------- */
.days-bubbles {
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
}

.day-bubble {
  width: 44px;
  height: 44px;
  border-radius: 50%;
  border: 2px solid #ddd;
  background: #f7f7f7;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 700;
  font-size: 14px;
  color: #333;
  cursor: pointer;
  transition: all 0.15s ease;
}

.day-bubble.active {
  background: linear-gradient(135deg,#6C63FF,#5E54E2);
  color: #fff;
  border-color: transparent;
  box-shadow: 0 4px 12px rgba(108,99,255,0.3);
}

.day-bubble:hover {
  transform: translateY(-2px);
}

/* ---------- Compact Slot ---------- */
.compact-slot {
  border-radius: 10px;
  border: 1px solid #eef1fb;
  background: #fff;
  padding: 12px;
  box-shadow: 0 2px 6px rgba(0,0,0,0.05);
  position: relative;
}

.slot-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px;
  padding-bottom: 10px;
  border-bottom: 1px solid #eef1fb;
}

.slot-number {
  font-weight: 700;
  font-size: 13px;
  color: #6C63FF;
}

.btn-delete-slot {
  background: #fee;
  color: #e11d48;
  border: none;
  padding: 4px 10px;
  border-radius: 6px;
  cursor: pointer;
  font-size: 12px;
  transition: all 0.2s;
}

.btn-delete-slot:hover {
  background: #fdd;
}

.slot-row {
  display: flex;
  gap: 12px;
}

.slot-col {
  flex: 1;
}

.slot-col label {
  display: block;
  font-size: 12px;
  margin-bottom: 6px;
  color: #333;
}

/* mode selector */
.compact-mode {
  display: flex;
  gap: 8px;
  align-items: center;
  flex-wrap: wrap;
}

.mode-btn {
  position: relative; /* Allow absolute positioning for badge */
  flex: 1;
  min-width: 120px;
  padding: 8px 10px;
  border-radius: 8px;
  font-size: 12px;
  font-weight: 600;
  border: 1px solid #e6e6ee;
  background: #fafafa;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  transition: all 0.12s ease;
}

.mode-btn.sleep-btn.active {
  background: #ef4444;
  color: #fff;
  border-color: #ef4444;
}

.mode-btn.limit-btn.active {
  background: #f59e0b;
  color: #fff;
  border-color: #f59e0b;
}

.mode-btn.apps-btn.active {
  background: #8b5cf6;
  color: #fff;
  border-color: #8b5cf6;
}

/* Disabled Mode Buttons */
.mode-btn.disabled-mode {
  position: relative;
  opacity: 0.6;
  cursor: not-allowed;
  background: #f5f5f5;
  color: #9ca3af;
  border-color: #e5e7eb;
}

.mode-btn.disabled-mode:hover {
  transform: none;
  background: #f5f5f5;
}

/* Coming Soon Badge */
.coming-soon-badge {
  position: absolute;
  top: -8px;
  right: -8px;
  background: linear-gradient(135deg, #f59e0b, #d97706);
  color: white;
  font-size: 9px;
  font-weight: 700;
  padding: 2px 6px;
  border-radius: 10px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  box-shadow: 0 2px 6px rgba(245, 158, 11, 0.4);
  animation: pulse-badge 2s infinite;
}

@keyframes pulse-badge {
  0%, 100% {
    transform: scale(1);
    opacity: 1;
  }
  50% {
    transform: scale(1.05);
    opacity: 0.9;
  }
}

/* Mode notes/descriptions */
.mode-note {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  padding: 10px 12px;
  border-radius: 8px;
  font-size: 13px;
}

.mode-note i {
  margin-top: 2px;
  flex-shrink: 0;
}

.mode-note-sleep {
  background: #fef2f2;
  border: 1px solid #fecaca;
  color: #991b1b;
}

.mode-note-limit {
  background: #fffbeb;
  border: 1px solid #fde68a;
  color: #92400e;
}

.mode-note-apps {
  background: #f5f3ff;
  border: 1px solid #ddd6fe;
  color: #6b21a8;
}

/* App Categories */
.app-categories {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.category-wrapper {
  border: 1px solid #eef1fb;
  border-radius: 8px;
  overflow: hidden;
}

.category-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 10px 12px;
  background: #fafafa;
  cursor: pointer;
  transition: background 0.2s;
}

.category-item:hover {
  background: #f0f0f5;
}

.category-item.expanded {
  background: #f5f3ff;
  border-bottom: 1px solid #eef1fb;
}

.category-info {
  display: flex;
  align-items: center;
  gap: 10px;
}

.category-icon {
  color: #6C63FF;
  font-size: 16px;
  width: 20px;
  text-align: center;
}

.category-name {
  font-size: 13px;
  font-weight: 600;
  color: #333;
}

.app-count {
  font-size: 11px;
  color: #999;
  margin-left: 4px;
}

.category-actions {
  display: flex;
  align-items: center;
  gap: 12px;
}

.expand-icon {
  color: #6C63FF;
  font-size: 12px;
  transition: transform 0.2s;
}

.expand-icon.rotated {
  transform: rotate(180deg);
}

/* Apps List */
.apps-list {
  background: #fff;
  padding: 8px;
}

.app-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px 12px;
  background: #fafafa;
  border-radius: 6px;
  margin-bottom: 6px;
  border: 1px solid #f0f0f5;
}

.app-item:last-child {
  margin-bottom: 0;
}

.app-name {
  font-size: 13px;
  color: #555;
}

/* Add More Button */
.btn-add-more {
  width: 100%;
  padding: 10px;
  border-radius: 8px;
  border: 2px dashed #6C63FF;
  background: #f5f3ff;
  color: #6C63FF;
  font-weight: 600;
  font-size: 13px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  transition: all 0.2s;
}

.btn-add-more:hover {
  background: #ede9fe;
  transform: translateY(-2px);
}

/* inputs */
.form-control-sm {
  border-radius: 8px;
  padding: 6px 10px;
}

/* Custom Switch */
.custom-control-label {
  cursor: pointer;
}


/* Overnight Schedule Info Box - Enhanced UX */
.overnight-info-create {
  margin-top: 16px;
  padding: 0;
  background: linear-gradient(135deg, #fffbeb 0%, #fef3c7 100%);
  border: 2px solid #fbbf24;
  border-radius: 12px;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  box-shadow: 0 4px 12px rgba(251, 191, 36, 0.15);
  animation: slideDown 0.3s ease-out;
}

@keyframes slideDown {
  from {
    opacity: 0;
    transform: translateY(-10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

/* Header section with icon and title */
.overnight-info-create .overnight-header {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 14px 16px;
  background: linear-gradient(135deg, #fbbf24 0%, #f59e0b 100%);
  border-bottom: 1px solid #f59e0b;
}

.overnight-info-create .overnight-header i.fa-moon {
  font-size: 1.3rem;
  color: #ffffff;
  flex-shrink: 0;
  filter: drop-shadow(0 2px 4px rgba(0, 0, 0, 0.1));
}

.overnight-info-create .overnight-header strong {
  color: #ffffff;
  font-size: 1rem;
  font-weight: 700;
  letter-spacing: 0.3px;
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.1);
}

/* Content section */
.overnight-text {
  padding: 16px;
}

.overnight-text p {
  color: #78350f;
  font-size: 0.9rem;
  margin-bottom: 12px;
  line-height: 1.6;
  font-weight: 500;
}

/* Example box */
.overnight-example {
  background: #ffffff;
  border: 1px solid #fde68a;
  border-radius: 8px;
  padding: 12px 14px;
  margin-top: 8px;
}

.overnight-example .example-label {
  display: flex;
  align-items: center;
  gap: 6px;
  color: #d97706;
  font-size: 0.85rem;
  font-weight: 700;
  margin-bottom: 8px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.overnight-example .example-label i {
  color: #f59e0b;
  font-size: 0.9rem;
}

.overnight-example .example-text {
  color: #92400e;
  font-size: 0.88rem;
  line-height: 1.6;
  margin: 0;
}

.overnight-example .example-text strong {
  color: #7c2d12;
  font-weight: 700;
  background: #fef3c7;
  padding: 2px 6px;
  border-radius: 4px;
  white-space: nowrap;
}

/* Responsive adjustments */
@media (max-width: 768px) {
  .overnight-info-create {
    margin-top: 12px;
  }
  
  .overnight-info-create .overnight-header {
    padding: 12px 14px;
  }
  
  .overnight-text {
    padding: 14px;
  }
  
  .overnight-example {
    padding: 10px 12px;
  }
}

.apps-flat-list {
  display: flex;
  flex-direction: column;
  gap: 6px;
  max-height: 320px;
  overflow-y: auto;
  padding-right: 4px;
}

.app-flat-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 9px 12px;
  background: #fafafa;
  border: 1px solid #f0f0f5;
  border-radius: 8px;
  transition: background 0.15s;
}

.app-flat-item:hover {
  background: #f5f3ff;
  border-color: #ddd6fe;
}

.app-flat-info {
  display: flex;
  align-items: center;
  gap: 10px;
}

.app-flat-icon {
  color: #6C63FF;
  font-size: 14px;
  width: 18px;
  text-align: center;
}
.app-flat-icon-img {
  width: 28px;
  height: 28px;
  border-radius: 6px;
  object-fit: cover;
  flex-shrink: 0;
}

.apps-flat-loader {
  display: flex;
  align-items: center;
  padding: 16px 12px;
  background: #fafafa;
  border: 1px solid #f0f0f5;
  border-radius: 8px;
}

.apps-flat-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  padding: 24px;
  background: #fafafa;
  border: 1px solid #f0f0f5;
  border-radius: 8px;
  color: #9ca3af;
  font-size: 13px;
}

.apps-flat-empty i {
  font-size: 24px;
}
</style>